import React from 'react';
import PropTypes from 'prop-types';
import { config } from '@uhg-abyss/web/tools/config';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Card } from '@uhg-abyss/web/ui/Card';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Box } from '@uhg-abyss/web/ui/Box';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { usePPDLagoon } from '@sde/widgets-core/src/tools/lagoon';
import { Text } from '@uhg-abyss/web/ui/Text';
import { event } from '@uhg-abyss/web/tools/event';
import { useHeaderMenu } from 'src/state/headerMenu';
import { WelcomeText } from '../WelcomeText';

const StyledPrefText = styled(Text, {
  '&.abyss-text-root': {
    display: 'inline-block',
    textAlign: 'center',
  },
});

const StyledStack = styled(Layout.Stack, {
  marginLeft: '20%',
  marginRight: '20%',
});

const DialogContainer = styled('div', {
  textAlign: 'center',
  marginTop: '50px',
});

const ModalTitle = styled(Heading, {
  '&.abyss-heading-root': {
    fontFamily: 'UHCSerif,Georgia Bold,serif',
    fontWeight: '$semibold',
    fontSize: '1.802rem',
    lineHeight: '2.25rem',
    textAlign: 'center',
  },
});

const PaddedBox = styled(Box, {
  '&.abyss-box-root': {
    padding: '50px 0px 50px',
  },
});

const MessageContainer = styled('div', {
  padding: '0px 20%',
});

const CenteredText = styled(Text, {
  '&.abyss-text-root': {
    display: 'inline-block',
  },
});

const BodyContainer = styled('div', {
  padding: '0 20px',
  textAlign: 'center',
});

const MarginTop = styled('div', {
  marginTop: 10,
});

const StyledCard = styled(Card, {
  width: '75%',
  margin: '0 auto',
  '@media (max-width: 820px)': {
    width: '90%',
  },
});

const ButtonContainer = styled('div', {
  marginBottom: '50px',
});

export const withButtonErrorCodes = [
  'DSA:V:USR:NU',
  'DSA:V:USR:IR',
  'DSA:V:USR:PR',
];

export const DigitalSecurityError = ({
  code,
  message,
  adminErrMessage,
  isProviderPreferenceError,
}) => {
  const { getErrorMessage } = usePPDLagoon();
  const providerPreferenceErrorMsg = getErrorMessage(
    'default-provider-pref-error'
  );

  const splitProviderPreferenceErrorMsg = providerPreferenceErrorMsg.split('|');
  const splitErrorMessage =
    (adminErrMessage || message || '').split('|') || providerPreferenceErrorMsg;

  const lagoonErrTitle = getErrorMessage(`ds-user-permission-title[${code}]`);
  const errorTitle = code ? lagoonErrTitle : '';
  const buttonHref =
    code === 'DSA:V:USR:NU'
      ? config('REGISTRATION_PAGE_URL')
      : '#/prefcenter/orgtaxIdAccess';

  const buttonText =
    code === 'DSA:V:USR:NU'
      ? 'Register your TIN'
      : 'Organization & Tax ID Access';

  const handleButtonClick = () => {
    if (buttonText === 'Register your TIN') {
      window.open(config('REGISTRATION_PAGE_URL'));
      event('EXIT_LINK_NAVIGATE', {
        linkText: buttonText,
        linkUrl: buttonHref,
      });
    } else {
      event('NAVIGATION_CLICK', { name: buttonText });
      window.location.href = '#/prefcenter/orgtaxIdAccess ';
    }
  };

  const { toggleProvider } = useHeaderMenu.actions();

  return (
    <DialogContainer>
      <MarginTop>
        <WelcomeText />
      </MarginTop>
      <StyledCard>
        <PaddedBox color="white">
          <Layout.Stack alignLayout="center" space={16}>
            <IconBrand icon="alert" size="xl" />
            <Choose>
              <When condition={isProviderPreferenceError}>
                <StyledStack space={26}>
                  <Heading display="h3">
                    {splitProviderPreferenceErrorMsg[0] || ''}
                  </Heading>
                  <StyledPrefText>
                    {splitProviderPreferenceErrorMsg[1] || ''}
                  </StyledPrefText>
                  <Button size="$lg" onClick={() => toggleProvider(true)}>
                    {splitProviderPreferenceErrorMsg[2] || ''}
                  </Button>
                </StyledStack>
              </When>
              <Otherwise>
                <ModalTitle offset={2}>{errorTitle}</ModalTitle>
              </Otherwise>
            </Choose>
          </Layout.Stack>
        </PaddedBox>
        <BodyContainer>
          <Choose>
            <When condition={isProviderPreferenceError} />
            <Otherwise>
              <Layout.Stack
                alignLayout="center"
                alignItems="center"
                space={20}
                grow
              >
                <MessageContainer>
                  <CenteredText>{splitErrorMessage[0] || ''}</CenteredText>
                  <CenteredText>{splitErrorMessage[1] || ''}</CenteredText>
                </MessageContainer>

                <If condition={withButtonErrorCodes.indexOf(code) > -1}>
                  <ButtonContainer>
                    <Button
                      size="$lg"
                      id="new-user-tin-access-page"
                      data-testid="new-user-tin-oops-modal"
                      aria-label="Click here to Register Tax ID access page"
                      onClick={handleButtonClick}
                    >
                      {buttonText}
                    </Button>
                  </ButtonContainer>
                </If>
              </Layout.Stack>
            </Otherwise>
          </Choose>
        </BodyContainer>
        <If condition={!isProviderPreferenceError} />
      </StyledCard>
    </DialogContainer>
  );
};

DigitalSecurityError.propTypes = {
  code: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  adminErrMessage: PropTypes.string,
  isProviderPreferenceError: PropTypes.bool,
};

DigitalSecurityError.defaultProps = {
  adminErrMessage: null,
  isProviderPreferenceError: false,
};
